import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, RefreshCw, AlertCircle, Printer } from "lucide-react";
import { toast } from "sonner";
import { getPrinters, isQZConnected, printReceipt, getSeparator, getCutCommand, ESCPOS } from "@/lib/qz-tray";
import type { PrinterWithJobTypes, JobType, PaperWidth, CutType, PrinterProtocol } from "@/hooks/usePrinters";
import { JOB_TYPE_LABELS } from "@/hooks/usePrinters";

interface PrinterFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  printer?: PrinterWithJobTypes | null;
  onSave: (
    name: string,
    printerName: string,
    isActive: boolean,
    jobTypes: JobType[],
    settings?: {
      paper_width?: PaperWidth;
      chars_per_line?: number;
      auto_cut_enabled?: boolean;
      cut_type?: CutType;
      extra_lines_before_cut?: number;
      protocol?: PrinterProtocol;
    }
  ) => Promise<boolean>;
}

const ALL_JOB_TYPES: JobType[] = [
  "conference_bill",
  "customer_receipt",
  "cash_withdrawal",
  "cash_supply",
  "cash_register_close",
  "order_production",
  "kitchen",
  "bar",
  "drinks",
  "portions",
  "cancellation_receipt",
  "partial_payment",
];

export function PrinterFormDialog({
  open,
  onOpenChange,
  printer,
  onSave,
}: PrinterFormDialogProps) {
  const [name, setName] = useState("");
  const [printerName, setPrinterName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>([]);
  const [availablePrinters, setAvailablePrinters] = useState<string[]>([]);
  const [isLoadingPrinters, setIsLoadingPrinters] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrintingTest, setIsPrintingTest] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Printer settings
  const [paperWidth, setPaperWidth] = useState<PaperWidth>("80mm");
  const [autoCutEnabled, setAutoCutEnabled] = useState(true);
  const [cutType, setCutType] = useState<CutType>("full");
  const [extraLinesBeforeCut, setExtraLinesBeforeCut] = useState(3);
  const [protocol, setProtocol] = useState<PrinterProtocol>("epson_standard");

  useEffect(() => {
    if (open) {
      if (printer) {
        setName(printer.name);
        setPrinterName(printer.printer_name);
        setIsActive(printer.is_active);
        setSelectedJobTypes(
          printer.job_types
            .filter((jt) => jt.is_enabled)
            .map((jt) => jt.job_type)
        );
        setPaperWidth(printer.paper_width || "80mm");
        setAutoCutEnabled(printer.auto_cut_enabled ?? true);
        setCutType(printer.cut_type || "full");
        setExtraLinesBeforeCut(printer.extra_lines_before_cut ?? 3);
        setProtocol(printer.protocol || "epson_standard");
      } else {
        setName("");
        setPrinterName("");
        setIsActive(true);
        setSelectedJobTypes([]);
        setPaperWidth("80mm");
        setAutoCutEnabled(true);
        setCutType("full");
        setExtraLinesBeforeCut(3);
        setProtocol("epson_standard");
      }
      loadPrinters();
    }
  }, [open, printer]);

  const loadPrinters = async () => {
    setIsLoadingPrinters(true);
    setConnectionError(null);
    try {
      console.log("Tentando carregar impressoras...");
      const printers = await getPrinters();
      console.log("Impressoras encontradas:", printers);
      
      if (printers.length === 0 && !isQZConnected()) {
        setConnectionError("QZ Tray não está conectado. Verifique se está instalado e em execução.");
      } else {
        setAvailablePrinters(printers);
      }
    } catch (error: any) {
      console.error("Erro ao carregar impressoras:", error);
      const errorMsg = error?.message || "Erro desconhecido";
      setConnectionError(`Erro: ${errorMsg}`);
      toast.error("Não foi possível carregar as impressoras. Verifique o QZ Tray.");
    } finally {
      setIsLoadingPrinters(false);
    }
  };

  const handleJobTypeToggle = (jobType: JobType) => {
    setSelectedJobTypes((prev) =>
      prev.includes(jobType)
        ? prev.filter((jt) => jt !== jobType)
        : [...prev, jobType]
    );
  };

  const handlePaperWidthChange = (value: PaperWidth) => {
    setPaperWidth(value);
  };

  const handleTestPrint = async () => {
    if (!printerName) {
      toast.error("Selecione uma impressora primeiro");
      return;
    }

    setIsPrintingTest(true);
    try {
      const charsPerLine = paperWidth === "58mm" ? 32 : 48;
      const separator = getSeparator(charsPerLine);
      const now = new Date();
      const dateStr = now.toLocaleDateString("pt-BR");
      const timeStr = now.toLocaleTimeString("pt-BR");

      let content = ESCPOS.INIT;
      content += ESCPOS.ALIGN_CENTER;
      content += ESCPOS.BOLD_ON;
      content += "*** PAGINA DE TESTE ***\n";
      content += ESCPOS.BOLD_OFF;
      content += ESCPOS.ALIGN_LEFT;
      content += separator;
      content += `Data: ${dateStr}\n`;
      content += `Hora: ${timeStr}\n`;
      content += separator;
      content += `Impressora: ${printerName}\n`;
      content += `Largura: ${paperWidth}\n`;
      content += `Caracteres/linha: ${charsPerLine}\n`;
      content += separator;
      content += ESCPOS.ALIGN_CENTER;
      content += "ALTUS FOOD\n";
      content += "Sistema de Gestao\n";
      content += ESCPOS.ALIGN_LEFT;
      content += separator;
      
      // Add cut command if enabled
      if (autoCutEnabled) {
        content += getCutCommand({ 
          paper_width: paperWidth,
          auto_cut_enabled: autoCutEnabled, 
          cut_type: cutType, 
          extra_lines_before_cut: extraLinesBeforeCut,
          chars_per_line: charsPerLine,
          protocol: protocol
        });
      }

      const success = await printReceipt(printerName, content, { 
        skipLog: true,
        settings: {
          paper_width: paperWidth,
          chars_per_line: charsPerLine,
          auto_cut_enabled: autoCutEnabled,
          cut_type: cutType,
          extra_lines_before_cut: extraLinesBeforeCut,
          protocol: protocol
        }
      });
      
      if (success) {
        toast.success("Página de teste enviada com sucesso!");
      }
    } catch (error) {
      console.error("Erro na impressão de teste:", error);
      toast.error("Erro ao imprimir página de teste");
    } finally {
      setIsPrintingTest(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome da impressora");
      return;
    }
    if (!printerName) {
      toast.error("Selecione uma impressora do sistema");
      return;
    }

    setIsSaving(true);
    const success = await onSave(name.trim(), printerName, isActive, selectedJobTypes, {
      paper_width: paperWidth,
      chars_per_line: paperWidth === "58mm" ? 32 : 48,
      auto_cut_enabled: autoCutEnabled,
      cut_type: cutType,
      extra_lines_before_cut: extraLinesBeforeCut,
      protocol: protocol,
    });
    setIsSaving(false);

    if (success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {printer ? "Editar Impressora" : "Nova Impressora"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome da Impressora</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Caixa, Cozinha, Bar..."
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="printer">Impressora do Sistema</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={loadPrinters}
                disabled={isLoadingPrinters}
              >
                {isLoadingPrinters ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Select value={printerName} onValueChange={setPrinterName}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a impressora" />
              </SelectTrigger>
              <SelectContent>
                {availablePrinters.length === 0 ? (
                  <SelectItem value="none" disabled>
                    {isLoadingPrinters
                      ? "Carregando..."
                      : "Nenhuma impressora encontrada"}
                  </SelectItem>
                ) : (
                  availablePrinters.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {connectionError ? (
              <div className="flex items-center gap-2 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />
                <span>{connectionError}</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Certifique-se que o QZ Tray está em execução
              </p>
            )}
          </div>

          {printer && (
            <div className="flex items-center justify-between">
              <Label htmlFor="active">Impressora Ativa</Label>
              <Switch
                id="active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          )}

          {/* Paper and Cut Settings */}
          <div className="space-y-3 rounded-md border p-3">
            <Label className="text-sm font-medium">Configurações de Papel e Corte</Label>
            
            <div className="space-y-2">
              <Label htmlFor="protocol" className="text-xs text-muted-foreground">Modelo/Protocolo</Label>
              <Select value={protocol} onValueChange={(v) => setProtocol(v as PrinterProtocol)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="epson_standard">Epson / Elgin / Tanca (Padrão)</SelectItem>
                  <SelectItem value="bematech_legacy">Bematech (Modelos Antigos)</SelectItem>
                  <SelectItem value="generic">Genérico (Sem Guilhotina)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Use "Padrão" para Epson TM-T20, Elgin i7/i9, Tanca e impressoras modernas
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paperWidth" className="text-xs text-muted-foreground">Largura do Papel</Label>
              <Select value={paperWidth} onValueChange={handlePaperWidthChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="80mm">80mm (48 caracteres)</SelectItem>
                  <SelectItem value="58mm">58mm (32 caracteres)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="autoCut" className="text-sm">Guilhotina Automática</Label>
              <Switch
                id="autoCut"
                checked={autoCutEnabled}
                onCheckedChange={setAutoCutEnabled}
              />
            </div>

            {autoCutEnabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cutType" className="text-xs text-muted-foreground">Tipo de Corte</Label>
                  <Select value={cutType} onValueChange={(v) => setCutType(v as CutType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Corte Total</SelectItem>
                      <SelectItem value="partial">Corte Parcial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="extraLines" className="text-xs text-muted-foreground">Linhas Antes do Corte</Label>
                  <Input
                    id="extraLines"
                    type="number"
                    min={0}
                    max={10}
                    value={extraLinesBeforeCut}
                    onChange={(e) => setExtraLinesBeforeCut(Number(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Linhas em branco para facilitar o destaque do papel
                  </p>
                </div>
              </>
            )}
            
            {/* Test Print Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full mt-2"
              onClick={handleTestPrint}
              disabled={!printerName || isPrintingTest}
            >
              {isPrintingTest ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Printer className="mr-2 h-4 w-4" />
              )}
              Imprimir Página de Teste
            </Button>
          </div>

          <div className="space-y-2">
            <Label>Tipos de Impressão</Label>
            <div className="space-y-2 rounded-md border p-3">
              {ALL_JOB_TYPES.map((jobType) => (
                <div key={jobType} className="flex items-center space-x-2">
                  <Checkbox
                    id={jobType}
                    checked={selectedJobTypes.includes(jobType)}
                    onCheckedChange={() => handleJobTypeToggle(jobType)}
                  />
                  <label
                    htmlFor={jobType}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {JOB_TYPE_LABELS[jobType]}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {printer ? "Salvar" : "Cadastrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
